/**
 * Google Cast integration.
 *
 * Queue strategy — browser-driven, not native Cast queue:
 *   The Cast SDK's native queue (QueueLoad) showed unreliable auto-advance in testing,
 *   so instead we load one item at a time and listen for IDLE state on the receiver
 *   to advance to the next chapter ourselves via loadMedia. _pendingItems holds the
 *   remaining MediaInfo objects; _advance() sends the next one when the receiver goes idle.
 *
 * Initialisation order:
 *   The Cast SDK script (cast_sender.js) calls window.__onGCastApiAvailable when ready.
 *   However, it fires that callback before the CAF framework (cast.framework) finishes
 *   loading, so initCast polls until cast.framework is available before registering
 *   listeners and setting up the RemotePlayer.
 */
import config from './config.js'
import { getBookChapterUrls } from './books.js'

const COVER_URL = `${location.origin}/cover.jpg`

// Remaining chapters to cast after the currently playing one.
let _pendingItems = []

function _advance() {
  if (_pendingItems.length === 0) return
  const session = cast.framework.CastContext.getInstance().getCurrentSession()
  if (!session) { _pendingItems = []; return }
  const next = _pendingItems.shift()
  session.loadMedia(new chrome.cast.media.LoadRequest(next))
    .catch(err => console.error('Cast advance failed:', err))
}

/**
 * Initialises the Cast SDK and registers availability/session callbacks.
 * Must be called once on app startup; the Cast SDK script invokes
 * window.__onGCastApiAvailable automatically — this is the SDK's required hook name.
 *
 * @param {{ onAvailable: (v: boolean) => void, onSessionChange: (v: boolean) => void }} callbacks
 *   onAvailable   — called with true when a Cast device is visible on the network
 *   onSessionChange — called with true when a session starts/resumes, false when it ends
 */
export function initCast({ onAvailable, onSessionChange }) {
  // __onGCastApiAvailable is the fixed global name the Cast SDK calls; cannot use addEventListener.
  window['__onGCastApiAvailable'] = (isAvailable) => {
    if (!isAvailable) return

    // The basic SDK fires this callback before the CAF framework finishes
    // loading, so poll until window.cast.framework is ready.
    const setup = () => {
      if (!window.cast?.framework) {
        setTimeout(setup, 50)
        return
      }

      const ctx = cast.framework.CastContext.getInstance()
      ctx.setOptions({
        receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      })

      ctx.addEventListener(
        cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        (e) => {
          onAvailable(e.castState !== cast.framework.CastState.NO_DEVICES_AVAILABLE)
        }
      )

      ctx.addEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (e) => {
          const { SESSION_STARTED, SESSION_RESUMED, SESSION_ENDED } = cast.framework.SessionState
          if (e.sessionState === SESSION_STARTED || e.sessionState === SESSION_RESUMED) {
            onSessionChange(true)
          } else if (e.sessionState === SESSION_ENDED) {
            _pendingItems = []
            onSessionChange(false)
          }
        }
      )

      // Advance to the next item when the receiver goes IDLE (chapter ended).
      const remotePlayer = new cast.framework.RemotePlayer()
      const rpc = new cast.framework.RemotePlayerController(remotePlayer)
      rpc.addEventListener(
        cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
        () => {
          if (remotePlayer.playerState === chrome.cast.media.PlayerState.IDLE) {
            _advance()
          }
        }
      )
    }

    setup()
  }
}

/**
 * Starts (or joins) a Cast session and begins playing from the given position.
 * Builds a flat list of all chapters from startBookIdx/startChapterIdx onward,
 * loads the first one immediately, and stores the rest in _pendingItems for
 * browser-driven advance via _advance().
 *
 * @param {string[]} books          - Ordered book names for the active list
 * @param {number}   startBookIdx   - Index into books[] to start from
 * @param {number}   startChapterIdx - 0-based chapter index within that book
 * @param {number}   [startTime=0]  - Seek position in seconds for the first chapter
 */
export async function castList(books, startBookIdx, startChapterIdx, startTime = 0) {
  const raw = config.storage.getBaseUrl()
  // Cast requires absolute URLs; resolve relative base URLs against the page origin.
  const baseUrl = raw.startsWith('http') ? raw : `${location.origin}${raw}`
  const items = []

  for (let bi = startBookIdx; bi < books.length; bi++) {
    const bookName = books[bi]
    const urls = getBookChapterUrls(bookName, baseUrl)
    const startCh = bi === startBookIdx ? startChapterIdx : 0

    for (let ci = startCh; ci < urls.length; ci++) {
      const media = new chrome.cast.media.MediaInfo(urls[ci], 'audio/mpeg')
      media.metadata = new chrome.cast.media.MusicTrackMediaMetadata()
      media.metadata.title = `${bookName} ${ci + 1}`
      media.metadata.albumName = 'Bible'
      media.metadata.images = [new chrome.cast.Image(COVER_URL)]

      const item = new chrome.cast.media.QueueItem(media)
      item.autoplay = true
      if (items.length === 0) item.startTime = startTime
      items.push(item)
    }
  }

  const ctx = cast.framework.CastContext.getInstance()
  if (!ctx.getCurrentSession()) {
    await ctx.requestSession()
  }

  const session = ctx.getCurrentSession()
  console.debug(`Cast: ${items.length} items, first URL = ${items[0].media.contentId}`)

  // Load the first item directly; stash the rest for _advance() to send one-by-one.
  _pendingItems = items.slice(1).map(item => item.media)
  await session.loadMedia(new chrome.cast.media.LoadRequest(items[0].media)).catch((err) => {
    console.error('Cast loadMedia failed:', err)
    _pendingItems = []
    throw err
  })
}

/** Ends the active Cast session and disconnects the receiver. */
export function stopCast() {
  cast.framework.CastContext.getInstance().endCurrentSession(true)
}
